import { useParams } from "react-router-dom";
import { EvtHeader, EvtSectionHeader } from "components/header";
import { fetchBillable } from "./api";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { EvtCoordinatorCard } from "components/coordinator/display-card";
import HeaderPortal from "components/portal/header-portal";
import { PermissionControlledInput } from "permissions/components/input";
import EditItemButton from "components/buttons/edit";
import { EvtStatsCard } from "components/stats/display-card";
import { DisplayDate } from "components/display/date";
import { DisplayHours } from "components/display/hours";
import { EvtStatsAmoundDisplayWithCurrencyCard } from "components/stats/display-amount-with-currency-card";
import { AnalyticsStatsCardWithSeparator } from "components/analytics/display-card-with-separator";
import { AnalyticsStatsCard } from "components/analytics/display-card";
import { ExtendedStatCard } from "./components/extension-stat";
import { usePermissions } from "contexts/permission";
import { useBillable } from "../../context";

export default function BillableDetails() {
  const { billableId } = useParams();
  const permissions = usePermissions();
  const { type } = useBillable();

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  const canReadExtensions = permissions.hasFieldAccess(
    "billables",
    "read",
    "extensions",
  );

  const canReadAttributedTo = permissions.hasFieldAccess(
    "billables",
    "read",
    "attributed_to",
  );

  const {
    data: billable,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["billables", "detail", billableId, canReadAmount],
    queryFn: () => fetchBillable({ billableId, includeAmount: canReadAmount }),
    enabled: !!billableId,
  });

  if (isLoading) return <div className="p-8">Loading...</div>;
  if (isError)
    return (
      <div className="p-8 text-red-500">Error loading billable details.</div>
    );

  return (
    <div className="flex-1">
      <EvtHeader title="Billable Details" />
      <section>
        <div className="grid grid-cols-3 px-4 gap-4 md:px-6 mt-2.5 min-h-24">
          {canReadAmount && (
            <EvtStatsAmoundDisplayWithCurrencyCard
              label="Amount"
              data={{
                value: billable.amount,
                currency: billable.currency.name,
              }}
              info={`Adjusted Amount: ${billable.adjusted_amount ?? 0}`}
            />
          )}
          <EvtStatsCard
            label="Billing Date"
            value={<DisplayDate value={billable.billing_date} />}
          />
          <EvtStatsCard
            label="Due Date"
            value={<DisplayDate value={billable.due_date} />}
          />
          <EvtStatsCard
            label="Invoice Date"
            value={<DisplayDate value={billable.invoice_date} />}
          />
          <EvtStatsCard
            label="Forecast Date"
            value={<DisplayDate value={billable.forecast_date} />}
          />
          <EvtStatsCard
            label="Collected Date"
            value={<DisplayDate value={billable.collected_date} />}
          />
          {canReadAmount &&
            <>
              {type === "hourly" && (
                <EvtStatsCard
                  label="Billable Hours"
                  value={<DisplayHours value={billable.hourly_billing_config.billable_hours} />}
                />
              )}
              {type === "dedicated" && (
                <>
                  <EvtStatsCard
                    label="Working Days"
                    value={<DisplayHours value={billable.dedicated_billing_config.working_days} />}
                  />
                  <EvtStatsCard
                    label="Billable Days"
                    value={<DisplayHours value={billable.dedicated_billing_config.billable_days} />}
                  />
                </>
              )}
            </>
          }
        </div>
      </section>
      <section>
        <div className="lg:flex gap-y-4 lg:gap-y-6 gap-x-6 items-center lg:flex-row grid sm:grid-cols-2 grid-cols-1 md:px-6 p-7">
          {canReadExtensions && <ExtendedStatCard billable={billable} />}
          <AnalyticsStatsCardWithSeparator
            label="Date Created"
            value={<DisplayDate value={billable.date_created} />}
            className="w-full max-w-full"
          />
          <AnalyticsStatsCard
            label="Date Updated"
            value={<DisplayDate value={billable.date_updated} />}
          />
        </div>
      </section>
      <section className="mt-3.5">
        <EvtSectionHeader title="Coordinators" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 px-4 gap-4 md:px-6 mt-2.5">
          <EvtCoordinatorCard
            collection="jobs"
            id={billable.job.id}
            role="Job Coordinator"
          />
          <EvtCoordinatorCard
            collection="projects"
            id={billable.job.project.id}
            role="Account Manager"
            type="PROJECT_WITH_SLUG"
          />
          <EvtCoordinatorCard
            collection="projects"
            id={billable.job.project.id}
            role="Sales Coordinator"
            type="PROJECT_WITH_SLUG"
          />
          {canReadAttributedTo && billable.attributed_to && (
            <EvtCoordinatorCard
              collection="billables"
              id={billable.id}
              role="Attributed To"
              type="ATTRIBUTED_TO"
            />
          )}
        </div>
      </section>
      {/* <BillableExtensions /> */}
      <HeaderPortal>
        <PermissionControlledInput
          rule="update:SC,AM,FIN"
          collection="billables"
        >
          <EditItemButton variant="ghost" />
        </PermissionControlledInput>
      </HeaderPortal>
    </div>
  );
}
